import _ from 'lodash';
import { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { promptToaster } from '../../../App/actions';
import moment from 'moment';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import {
  Button,
  SubmitButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';

import CreateCentralizedReportForm from '../CreateCentralizedReportForm';
import CreateNewCentralizedReportForm from '../CreateNewCentralizedReportForm';
import ReportsService from '../../../../services/reportService';
import PropertyGroupService from '../../../../services/propertyGroupService';

const CreateCentralizedReportModal = ({
  isOpen,
  closeModal,
  reportName,
  version = 'legacy',
  reportId,
  organizationId,
  currentUser,
  promptToaster,
  zip,
  projectNames,
  configuration,
  userProperties = [],
  onReportQueued,
  onResetSelectedReport,
}) => {
  const [projectName, setProjectName] = useState(null);
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [dateRangeType, setDateRangeType] = useState('createdAt');
  const [year, setYear] = useState(null);
  const variant = reportName === 'CHART Connect' ? 'year' : 'date-range';
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const formRef = useRef(null);

  const handleValidationChange = (isValid) => {
    setIsFormValid(isValid);
  };

  const isWorkOrderReport = reportName === 'Work Orders';
  const isCHARTReport = reportName === 'CHART Connect';
  const reportsService = new ReportsService();
  const queryClient = useQueryClient();
  const isNewReport = version === 'new';
  const hasPropertyGroupsFilter = configuration?.propertyGroupsFilter === true;
  const hasEligibilityFilter = configuration?.eligibilityFilter === true;
  const userId = currentUser?.user?.id;

  const { data: eligibleProperties = [] } = useQuery(
    ['eligibleReportProperties', organizationId, userId, reportId],
    () =>
      reportsService.getEligibleReportProperties({
        organizationId,
        userId,
        reportReferenceId: reportId,
      }),
    {
      enabled:
        isOpen &&
        isNewReport &&
        hasEligibilityFilter &&
        !!organizationId &&
        !!userId &&
        !!reportId,
      refetchOnWindowFocus: false,
      onError: () => {
        promptToaster({
          type: 'error',
          message: 'Error fetching eligible properties',
          title: 'Error',
        });
      },
    },
  );

  const formUserProperties = hasEligibilityFilter
    ? eligibleProperties
    : userProperties;

  const { data: userPropertyGroups = [] } = useQuery(
    ['userPropertyGroups', organizationId, userId],
    async () => {
      const propertyGroupService = new PropertyGroupService();
      const groups = await propertyGroupService.getUserPropertyGroups(
        organizationId,
      );
      return groups || [];
    },
    {
      enabled:
        isOpen &&
        isNewReport &&
        hasPropertyGroupsFilter &&
        !!organizationId &&
        !!userId,
      refetchOnWindowFocus: false,
      onError: () => {
        promptToaster({
          type: 'error',
          message: 'Error fetching property groups',
          title: 'Error',
        });
      },
    },
  );

  const createReportMutation = useMutation(
    (payload) => reportsService.createCentralizedReport(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['CentralizedReports']);
        promptToaster({
          type: 'info',
          message:
            'Report generation started. You will be notified when it is ready.',
          title: 'Enqueued',
          options: {
            showCloseButton: true,
            removeOnHover: true,
          },
        });
        if (onReportQueued) onReportQueued();
        resetForm();
      },
      onError: () => {
        promptToaster({
          type: 'error',
          message: 'Error creating report',
          title: 'Error',
        });
      },
    },
  );

  const shouldDisableCreate = isNewReport
    ? !isFormValid
    : (variant === 'date-range' && !dateRangeStart) ||
      (variant === 'year' && !year) ||
      (isCHARTReport && !projectName);

  // Handle form submission with validated data
  const handleValidatedSubmit = (formValues) => {
    const orgId = organizationId;
    if (!orgId) return resetForm();

    const payload = {
      organizationId: orgId,
      reportId,
      version: 'new',
    };

    // The timeSelection radio group is only rendered when a report offers more
    // than one option. With a single option the field never reaches formValues,
    // so it is taken from the configuration instead — otherwise the chosen year
    // or date would be dropped from the payload.
    const configuredTimeSelections = configuration?.timeSelectionOptions ?? [];
    const effectiveTimeSelection =
      formValues.timeSelection ||
      (configuredTimeSelections.length === 1
        ? configuredTimeSelections[0]
        : null);

    if (effectiveTimeSelection) {
      payload.timeSelection = effectiveTimeSelection.toUpperCase();

      // Add year or date range based on timeSelection
      const timeSelectionValue = effectiveTimeSelection.toLowerCase();

      if (timeSelectionValue === 'year' && formValues.year) {
        payload.year = formValues.year;
      } else if (
        timeSelectionValue === 'range' &&
        formValues.dateFrom &&
        formValues.dateTo
      ) {
        payload.dateFrom = formValues.dateFrom;
        payload.dateTo = formValues.dateTo;
      } else if (timeSelectionValue === 'date' && formValues.date) {
        payload.date = formValues.date;
      }
    }

    if (formValues.format) {
      payload.fileType = formValues.format;
    }

    if (formValues.propertyGroup) {
      const selectedGroup = userPropertyGroups.find(
        (group) => group.id === formValues.propertyGroup,
      );
      payload.propertyGroup = {
        id: formValues.propertyGroup,
        name: selectedGroup?.name || undefined,
      };
    }

    if (_.isArray(formValues.properties) && formValues.properties?.length > 0) {
      payload.properties = formValues.properties.map((propId) => {
        const propOption = propertyOptions.find((opt) => opt.value === propId);
        return {
          id: propId,
          name: propOption?.text || undefined,
        };
      });
    } else {
      // a string
      const propOption = propertyOptions.find(
        (opt) => opt.value === formValues.properties,
      );
      payload.properties = [
        {
          id: formValues.properties,
          name: propOption?.text || undefined,
        },
      ];
    }

    if (formValues.exportAs) {
      payload.exportAs = formValues.exportAs;
    }

    createReportMutation.mutate(payload);
  };

  async function handleCreateReport() {
    if (isNewReport) {
      if (formRef.current) {
        formRef.current.submit();
      }
      return;
    }

    // Legacy report creation
    const payload = {
      dateRangeStart,
      dateRangeEnd: dateRangeEnd || moment(),
      year,
      organizationId,
      userId: currentUser?.user?.id,
      username: currentUser?.user?.username,
      fortressReportId: reportId,
      variant,
      dateRangeType,
      projectName,
      fileType: isCHARTReport ? 'xlsm' : 'csv',
    };

    createReportMutation.mutate(payload);
  }
  function handleCancel() {
    resetForm();
  }
  function resetForm() {
    setProjectName(null);
    setDateRangeStart(null);
    setDateRangeEnd(null);
    setYear(null);
    setPropertyOptions([]);
    setIsFormValid(false);

    if (onResetSelectedReport) onResetSelectedReport();
    closeModal();
  }
  const actions = (
    <>
      <Button
        variant="text"
        onClick={handleCancel}
        disabled={createReportMutation.isLoading}
      >
        Cancel
      </Button>
      <SubmitButton
        variant="primary"
        onClick={handleCreateReport}
        disabled={shouldDisableCreate || createReportMutation.isLoading}
        isSubmitting={createReportMutation.isLoading}
      >
        Create
      </SubmitButton>
    </>
  );

  return (
    <>
      <Modal
        actions={actions}
        open={isOpen}
        onClose={resetForm}
        title={
          // A name that already ends in "Report" is not given a second one.
          /\breport$/i.test(String(reportName ?? '').trim())
            ? `Create ${reportName}`
            : `Create ${reportName} Report`
        }
        ModalProps={{
          PaperProps: {
            sx: {
              width: { xs: 'calc(100% - 32px)', sm: 710 },
              maxWidth: 710,
            },
          },
        }}
      >
        {isNewReport ? (
          <CreateNewCentralizedReportForm
            ref={formRef}
            organizationId={organizationId}
            configuration={configuration}
            onPropertyOptionsChange={setPropertyOptions}
            userProperties={formUserProperties}
            userPropertyGroups={userPropertyGroups}
            onValidationChange={handleValidationChange}
            onSubmit={handleValidatedSubmit}
          />
        ) : (
          <CreateCentralizedReportForm
            dateRangeStart={dateRangeStart}
            dateRangeEnd={dateRangeEnd}
            year={year}
            setDateRangeStart={setDateRangeStart}
            setDateRangeEnd={setDateRangeEnd}
            setYear={setYear}
            variant={variant}
            dateRangeType={dateRangeType}
            setDateRangeType={setDateRangeType}
            isWorkOrderReport={isWorkOrderReport}
            isCHARTReport={isCHARTReport}
            projectNames={projectNames}
            projectName={projectName}
            setProjectName={setProjectName}
          />
        )}
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    currentUser: state?.app?.currentUser,
  };
};

export default connect(mapStateToProps, { promptToaster })(
  CreateCentralizedReportModal,
);
